import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { TableRow, TableCell } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Chart from '../../../components/Chart';
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import ChartButton from '../../../components/Button/ChartButton';
import TableComponent from '../../../components/Table/TableComponent';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import { ClearButton } from '../../../components/Button/iconbutton';
import { BloodKetone, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const BloodKetoneForm: React.FC<{ patientId: string }> = ({ patientId }) => {
 
  const [bloodKetoneRecords, setBloodKetoneRecords] = useState<BloodKetone[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showChart, setShowChart] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
  const [showClearFilterButton, setShowClearFilterButton] = useState(false);


  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    fetchBloodKetoneRecords();
    setShowClearFilterButton(!!(searchTerm || (dateRange.startDate && dateRange.endDate)));
  }, [page, rowsPerPage, searchTerm, dateRange]);

  const fetchBloodKetoneRecords = async (startDate = '', endDate = '') => {
    setLoading(true);
    try {
      const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
        params: {
          limit: rowsPerPage,
          page: page + 1,
          from: startDate,
          to: endDate,
          order_by: 'details->date',
          dir: 2,
          user_id: patientId,
          slug: 'keytone',
          searchkey: searchTerm || undefined,
        },
        headers: getAxiosConfig().headers,
      });

      const vitalsData = response.data.data.vitals;
      const total = response.data.data.pagination?.total;

      if (Array.isArray(vitalsData)) {
        const formattedData: BloodKetone[] = vitalsData.map((vital) => ({
          id: vital.id,
          user_id: vital.user_id,
          consult_id: vital.consult_id,
          peripheral_id: vital.peripheral_id,
          slug: 'keytone',
          details: vital.details,
          freeze: vital.freeze,
          document: vital.document,
        }));
        setBloodKetoneRecords(formattedData);
        setTotalRecords(total ?? 0);
      } else {
        setBloodKetoneRecords([]);
        setTotalRecords(0);
      }
    } catch (error) {
      showSnackbar('Failed to fetch blood ketone records. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setPage(0);
    fetchBloodKetoneRecords();
    setShowClearFilterButton(false);
  };

  const formatDate = (dateString: string) => format(new Date(dateString), 'dd-MM-yyyy');

  const renderRow = (item: BloodKetone, index: number) => (
    <TableRow key={index}>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{page * rowsPerPage + index + 1}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{formatDate(item.details.date)}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.time}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.keytone}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
        <span
          style={{
            backgroundColor: item.details.range_code,
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#FFFFFF'
          }}
        >
          {item.details.keytoneFlag}
        </span>
      </TableCell>
      <TableCell>
     
      </TableCell>
    </TableRow>
  );

  const chartData = {
    labels: bloodKetoneRecords.map(record => record.details.date),
    datasets: [
      {
        label: 'Blood Ketone Level (mmol/L)',
        data: bloodKetoneRecords.map(record => record.details.keytone),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    animations: {
      tension: {
        easing: 'linear',
        from: 0.4, // Start tension
        to: 0.0,   // End tension
        // loop: true,
      },
    },
  };

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId="BloodKetone_Start_Date"
            endDateId="BloodKetone_End_Date"
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates)
            }}
            value={dateRange}
          />
          
          <IconButton
            icon={<SearchIcon />}
            onClick={() => fetchBloodKetoneRecords(dateRange.startDate, dateRange.endDate)}
          />
          {showClearFilterButton && <ClearButton onClick={handleClearFilters} />}
        </div>
        <div className="flex space-x-4">
       
        {bloodKetoneRecords.length > 0 && (
                    <ChartButton text={showChart ? 'List' : 'Chart'} onClick={() => setShowChart(prev => !prev)} />
                )}
        
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
      <TableComponent
        headers={['S.No', 'Date', 'Time', 'Value(mmol/L)', 'Result']}
        data={bloodKetoneRecords}
        loading={loading}
        renderRow={renderRow}
      />
      
            </>
      )}
      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
   
    </div>
  );
};

export default BloodKetoneForm;
